import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Popconfirm,
  message,
  Card,
  Typography,
  Switch,
  Divider,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import {
  adminRoleService,
  AdminRole,
  AdminRoleType,
  Permission,
} from '../services/adminRoleService';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const AdminRoles: React.FC = () => {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<AdminRole | null>(null);
  const [form] = Form.useForm();

  const availableResources = [
    { value: 'users', label: 'Users' },
    { value: 'posts', label: 'Posts' },
    { value: 'reports', label: 'Reports' },
    { value: 'keywords', label: 'Keywords' },
    { value: 'badges', label: 'Badges' },
    { value: 'notifications', label: 'Notifications' },
    { value: 'analytics', label: 'Analytics' },
    { value: 'pages', label: 'CMS Pages' },
    { value: 'faqs', label: 'FAQs' },
    { value: 'categories', label: 'Categories' },
    { value: 'breeds', label: 'Breeds' },
    { value: 'sliders', label: 'Sliders' },
  ];

  const availableActions = [
    { value: 'read', label: 'Read' },
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'suspend', label: 'Suspend' },
    { value: 'ban', label: 'Ban' },
    { value: 'reject', label: 'Reject' },
    { value: 'assign', label: 'Assign' },
    { value: 'remove', label: 'Remove' },
    { value: 'send', label: 'Send' },
    { value: 'resolve', label: 'Resolve' },
  ];

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await adminRoleService.getAllRoles();
      setRoles(data);
    } catch (error: any) {
      message.error('Failed to load roles: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRole(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (role: AdminRole) => {
    setEditingRole(role);
    form.setFieldsValue({
      name: role.name,
      type: role.type,
      description: role.description,
      permissions: role.permissions,
      isActive: role.isActive,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await adminRoleService.deleteRole(id);
      message.success('Role deleted successfully');
      loadRoles();
    } catch (error: any) {
      message.error('Failed to delete role: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingRole) {
        await adminRoleService.updateRole(editingRole._id, values);
        message.success('Role updated successfully');
      } else {
        await adminRoleService.createRole(values);
        message.success('Role created successfully');
      }
      
      setModalVisible(false);
      loadRoles();
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation errors
        return;
      }
      message.error('Failed to save role: ' + (error.message || 'Unknown error'));
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: AdminRoleType) => {
        const colors: Record<AdminRoleType, string> = {
          [AdminRoleType.SUPER_ADMIN]: 'red',
          [AdminRoleType.MODERATOR]: 'orange',
          [AdminRoleType.SUPPORT_ADMIN]: 'blue',
        };
        return <Tag color={colors[type]}>{type.replace('_', ' ').toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Permissions',
      key: 'permissions',
      render: (record: AdminRole) => (
        <span>{record.permissions.length} resource(s)</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: AdminRole) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this role?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>Admin Roles & Permissions</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Create Role
          </Button>
        </div>

        <Table
          dataSource={roles}
          columns={columns}
          rowKey="_id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingRole ? 'Edit Role' : 'Create Role'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isActive: true,
            permissions: [],
          }}
        >
          <Form.Item
            name="name"
            label="Role Name"
            rules={[{ required: true, message: 'Please enter role name' }]}
          >
            <Input placeholder="e.g., Content Moderator" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Role Type"
            rules={[{ required: true, message: 'Please select role type' }]}
          >
            <Select placeholder="Select role type" disabled={!!editingRole}>
              <Option value={AdminRoleType.SUPER_ADMIN}>Super Admin</Option>
              <Option value={AdminRoleType.MODERATOR}>Moderator</Option>
              <Option value={AdminRoleType.SUPPORT_ADMIN}>Support Admin</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Describe the role's responsibilities" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Divider>Permissions</Divider>

          <Form.List name="permissions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'resource']}
                        label="Resource"
                        rules={[{ required: true, message: 'Please select resource' }]}
                      >
                        <Select placeholder="Select resource">
                          {availableResources.map(resource => (
                            <Option key={resource.value} value={resource.value}>
                              {resource.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'actions']}
                        label="Actions"
                        rules={[{ required: true, message: 'Please select at least one action' }]}
                      >
                        <Checkbox.Group>
                          {availableActions.map(action => (
                            <Checkbox key={action.value} value={action.value}>
                              {action.label}
                            </Checkbox>
                          ))}
                        </Checkbox.Group>
                      </Form.Item>

                      <Button
                        type="link"
                        danger
                        onClick={() => remove(name)}
                        style={{ padding: 0 }}
                      >
                        Remove Permission
                      </Button>
                    </Space>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Add Permission
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminRoles;

